import { Search, X, RefreshCw } from 'lucide-react';
import type { ColumnDef, VisibilityState, RowData } from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils.js';
import { DataTableColumnToggle } from './data-table-column-toggle.js';
import type { DataTableColumnVisibilityStore } from './types.js';

export interface DataTableToolbarProps<TData extends RowData> {
  columns: ColumnDef<TData>[];
  hasSearch: boolean;
  searchInputValue: string;
  onSearchChange: (v: string) => void;
  onSearchClear: () => void;
  showColumnToggle: boolean;
  userOverrides: DataTableColumnVisibilityStore;
  columnVisibility: VisibilityState;
  onColumnToggle: (columnId: string, visible: boolean) => void;
  onColumnReset: () => void;
  onRefresh?: () => void;
  isFetching?: boolean;
}

export function DataTableToolbar<TData extends RowData>({
  columns,
  hasSearch,
  searchInputValue,
  onSearchChange,
  onSearchClear,
  showColumnToggle,
  userOverrides,
  columnVisibility,
  onColumnToggle,
  onColumnReset,
  onRefresh,
  isFetching,
}: DataTableToolbarProps<TData>) {
  if (!hasSearch && !showColumnToggle && !onRefresh) return null;

  return (
    <div className="flex items-center gap-3 mb-3">
      {hasSearch && (
        <div className="relative flex items-center max-w-xs w-full">
          <Search className="absolute left-2.5 size-3.5 text-muted-foreground pointer-events-none" />
          <Input
            placeholder="Search..."
            value={searchInputValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="h-8 pl-8 pr-8 text-sm"
          />
          {searchInputValue && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
              onClick={onSearchClear}
              aria-label="Clear search"
            >
              <X className="size-3.5" />
            </Button>
          )}
        </div>
      )}
      {(onRefresh || showColumnToggle) && (
        <div className="ml-auto flex items-center gap-2">
          {onRefresh && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={onRefresh}
              disabled={isFetching}
              aria-label="Refresh"
            >
              <RefreshCw className={cn('size-3.5', isFetching && 'animate-spin')} />
            </Button>
          )}
          {showColumnToggle && (
            <DataTableColumnToggle
              columns={columns}
              userOverrides={userOverrides}
              columnVisibility={columnVisibility}
              onToggle={onColumnToggle}
              onReset={onColumnReset}
            />
          )}
        </div>
      )}
    </div>
  );
}
